import { useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useNodeStore } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { CustomNode } from '@/types/node-types';
import {
  canGroupSelection,
  expandGroupNodes,
  findNewGroupId,
  getGroupEligibleSelection,
  getSharedGroupParentId,
  groupSelectedNodes,
  syncGroupNodeInteractionState,
  ungroupNodesFromContext,
  withOnlyGroupChildrenSelected,
} from '@/pages/workflow/helper/group-nodes';

/**
 * Selects a group's eligible children and deselects everything else, so
 * keyboard Delete/Backspace and the Ungroup toolbar's Copy/Duplicate/Delete
 * act on the same nodes. Shared by GroupNode's focus handler, the Ungroup
 * toolbar's actions, and groupSelected below. WorkflowEditorPage's
 * onSelectionChange independently re-derives a group's eligible-children ids
 * to decide whether to keep it focused, so no extra signal needs to be
 * threaded through here.
 */
export function selectGroupChildren(groupId: string) {
  const store = useNodeStore.getState();
  store.setNodes(withOnlyGroupChildrenSelected(store.nodes, groupId));
}

export function useNodeGrouping() {
  const { getNodesBounds, getNodes, getInternalNode } = useReactFlow();
  const saveSnapshot = useNodeStore((state) => state.saveSnapshot);
  const { canGroup, eligibleCount } = useNodeStore(
    useShallow((state) => ({
      canGroup:
        canGroupSelection(state.nodes) && !getSharedGroupParentId(state.nodes),
      eligibleCount: getGroupEligibleSelection(state.nodes).length,
    })),
  );

  const groupSelected = useCallback(() => {
    const nodes = getNodes() as CustomNode[];
    const selectedIds = getGroupEligibleSelection(nodes).map((node) => node.id);
    if (selectedIds.length < 2) return;

    const nextNodes = groupSelectedNodes(nodes, selectedIds, {
      getInternalNode,
      getNodesBounds,
    });
    // groupSelectedNodes returns the input unchanged (same reference) when
    // grouping fails (e.g. missing internal node data). Bail out before
    // expandGroupNodes/setState/saveSnapshot — otherwise a failed grouping
    // attempt could still collapse an unrelated single-child group
    // elsewhere on the canvas, clear focus, and push a no-op undo step.
    if (nextNodes === nodes) return;

    // groupSelectedNodes creates the container without width/height —
    // expandGroupNodes (normally run from onNodesChange) sizes it to its
    // children so the chrome doesn't render unsized until a later change.
    const expandedNodes = expandGroupNodes(nextNodes);

    // Focus the new container immediately so its chrome is visible right
    // after grouping, instead of staying hidden until the user clicks a node.
    const newGroupId = findNewGroupId(nodes, expandedNodes);

    useNodeStore.setState({
      groupContainerFocusedId: newGroupId,
      nodes: syncGroupNodeInteractionState(expandedNodes, newGroupId),
    });
    // Select the new group's children too, so keyboard Delete/Backspace
    // works immediately after grouping from the toolbar, matching the
    // padding-click focus path (groupSelectedNodes leaves them deselected).
    if (newGroupId) {
      selectGroupChildren(newGroupId);
    }
    saveSnapshot();
  }, [getInternalNode, getNodes, getNodesBounds, saveSnapshot]);

  const ungroupFromContext = useCallback(
    (contextNodeId?: string | null) => {
      const nodes = getNodes() as CustomNode[];
      const nextNodes = ungroupNodesFromContext(nodes, contextNodeId);
      // ungroupNodesFromContext returns the input unchanged (same reference)
      // when there's nothing to ungroup. Bail out before setState/saveSnapshot
      // — otherwise a no-op ungroup could still clear container focus and
      // push an empty undo step.
      if (nextNodes === nodes) return;

      useNodeStore.setState({
        groupContainerFocusedId: null,
        nodes: syncGroupNodeInteractionState(nextNodes, null),
      });
      saveSnapshot();
    },
    [getNodes, saveSnapshot],
  );

  return {
    groupSelected,
    ungroupFromContext,
    canGroup,
    eligibleCount,
  };
}
